import { useEffect, useRef } from "react";
import api from "@/api";

// Phase G (Feb 2026): Live GPS ping loop for salesman + manager.
// Pings backend every 30s OR when moved >50m. Auto-pauses if user is checked-out.
// Battery-friendly: uses watchPosition + interval-throttle.
export default function useGpsTracking({ enabled }) {
  const lastSentRef = useRef({ lat: null, lng: null, ts: 0 });

  useEffect(() => {
    if (!enabled) return;
    if (!("geolocation" in navigator)) return;

    let watchId = null;
    let intervalId = null;

    const send = async (pos) => {
      const { latitude, longitude, accuracy, speed, heading } = pos.coords;
      // Battery info if available
      let battery_level = null;
      try {
        if (navigator.getBattery) {
          const b = await navigator.getBattery();
          battery_level = Math.round(b.level * 100);
        }
      } catch {}
      try {
        await api.post("/tracking/ping", {
          latitude, longitude, accuracy: accuracy || null,
          speed: speed != null ? speed : null,
          heading: heading != null ? heading : null,
          battery_level,
        });
        lastSentRef.current = { lat: latitude, lng: longitude, ts: Date.now() };
      } catch {}
    };

    const tryPing = () => {
      if (document.hidden) return; // skip when tab hidden — saves battery
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { lat: prevLat, lng: prevLng, ts: prevTs } = lastSentRef.current;
          const now = Date.now();
          // Throttle: send if first ping OR >30s elapsed OR moved >50m
          let shouldSend = !prevTs || (now - prevTs) > 30000;
          if (!shouldSend && prevLat != null) {
            const R = 6371000;
            const toRad = (d) => d * Math.PI / 180;
            const dp = toRad(pos.coords.latitude - prevLat);
            const dl = toRad(pos.coords.longitude - prevLng);
            const a = Math.sin(dp/2)**2 + Math.cos(toRad(prevLat)) * Math.cos(toRad(pos.coords.latitude)) * Math.sin(dl/2)**2;
            const dist = 2 * R * Math.asin(Math.sqrt(a));
            shouldSend = dist > 50;
          }
          if (shouldSend) send(pos);
        },
        () => { /* ignore errors silently — fallback handled server-side */ },
        { enableHighAccuracy: false, maximumAge: 15000, timeout: 10000 }
      );
    };

    // Send first ping immediately, then every 30s
    tryPing();
    intervalId = setInterval(tryPing, 30000);

    return () => {
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [enabled]);
}
